import { constantTimeEqual } from './shared.ts'

export type AnyInngestPayload = Record<string, unknown>

const errorMessage = (error: unknown) => {
  return error instanceof Error ? error.message : 'Unknown error'
}

// Create a deep copy of the payload and remove _inngest and _signature fields
const _cleanPayload = (payload: AnyInngestPayload): AnyInngestPayload => {
  return Object.fromEntries(
    Object.entries(structuredClone(payload)).filter(
      ([key]) => !['_inngest', '_signature'].includes(key)
    )
  )
}

const _sign = async (signingKey: string, baseString: string) => {
  const enc = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(signingKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    enc.encode(baseString)
  )

  return Buffer.from(signature).toString('hex')
}

/**
 * Serializes Date objects to ISO datetime strings and canonicalizes the object
 * by recursively sorting all keys. This ensures consistent date serialization
 * and JSON outputs regardless of property insertion order for Inngest event signing.
 *
 * @internal
 * @param obj - The object to serialize and canonicalize
 * @returns A new object with all Date objects converted to ISO strings and keys sorted recursively
 */
const _serializeAndCanonicalize = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) {
    return obj
  }

  // Convert Date objects to ISO 8601 strings
  if (obj instanceof Date) {
    return obj.toISOString()
  }

  // Handle arrays recursively
  if (Array.isArray(obj)) {
    return obj.map(_serializeAndCanonicalize)
  }

  // Handle plain objects recursively
  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: AnyInngestPayload = {}
    // Sort keys to ensure canonical order
    for (const key of Object.keys(obj).sort()) {
      result[key] = _serializeAndCanonicalize((obj as AnyInngestPayload)[key])
    }
    return result
  }

  // Return primitives and other types as-is
  return obj
}

const _signEventPayload = async (
  payload: AnyInngestPayload,
  signingKey: string
) => {
  // Serialize dates and canonicalize the payload to ensure consistent key ordering
  const canonicalPayload = _serializeAndCanonicalize(payload)

  // Stringify the canonicalized payload for signing
  const payloadString = JSON.stringify(canonicalPayload)

  return await _sign(signingKey, payloadString)
}

export const signAsyncEventPayload = async (
  payload: AnyInngestPayload,
  signingKey: string
): Promise<AnyInngestPayload> => {
  // Validate signing secret
  if (!signingKey || signingKey.trim().length === 0) {
    throw new Error('Signing secret is required and must be a non-empty string')
  }

  if (!payload) {
    throw new Error('Payload is required')
  }

  try {
    const cleanPayload = _cleanPayload(payload)

    // Sign the payload (dates will be serialized and object will be canonicalized in _signEventPayload)
    const _signature = await _signEventPayload(cleanPayload, signingKey)

    // Apply the same serialization and canonicalization to the payload for return
    const serializedPayload = _serializeAndCanonicalize(
      cleanPayload
    ) as AnyInngestPayload

    return { ...serializedPayload, _signature }
  } catch (error) {
    if (error instanceof Error && error.message.includes('Signing secret')) {
      // Re-throw validation errors
      throw error
    }
    throw new Error(`Failed to sign payload: ${errorMessage(error)}`, {
      cause: error,
    })
  }
}

export const verifyAsyncEventSignature = async (
  payload: AnyInngestPayload,
  signingKey: string
): Promise<{
  valid: boolean
  error?: string
  cleanPayload?: AnyInngestPayload
}> => {
  // Validate signing secret
  if (!signingKey || signingKey.trim().length === 0) {
    return {
      valid: false,
      error: 'Signing secret is required and must be a non-empty string',
    }
  }

  if (!payload) {
    return {
      valid: false,
      error: 'Payload is required',
    }
  }

  const signature = payload._signature

  // Check required fields
  if (typeof signature !== 'string') {
    return {
      valid: false,
      error: 'Missing or invalid signature field (_signature)',
    }
  }

  try {
    const cleanPayload = _cleanPayload(payload)

    // Sign the clean payload (dates will be serialized and object will be canonicalized)
    const expectedSignature = await _signEventPayload(cleanPayload, signingKey)

    return constantTimeEqual(signature, expectedSignature)
      ? { valid: true, cleanPayload }
      : { valid: false, error: 'Signature verification failed' }
  } catch (error) {
    return {
      valid: false,
      error: `Failed to process payload: ${errorMessage(error)}`,
    }
  }
}
